<template>
  <div
    class="slider"
    tabindex="0"
    @touchstart="onTouchStart"
    @keydown="onKeyDown"
  >
    <div class="slider__stage">
      <button
        v-if="showNavElements"
        class="slider__button"
        :disabled="isTransitioning"
        data-track-text="appScreens.slider.next"
        @click="onClick('previous')"
      >
        <dotcom-icon
          name="chevron-right"
          art="regular"
          class="slider__caret-icon"
          rotation="180"
          :color="
            isPreviousDisabled
              ? '--c-secondary-neutral-2'
              : '--c-primary-neutral-1'
          "
          hover-color="--c-primary-color-2"
        />
      </button>

      <div class="slider__content">
        <SliderMask>
          <SliderMedia
            :slides="paddedSlides"
            :current-index="realIndex"
            :video-fallback-text="videoFallbackText"
            :show-transitions="showTransitions"
          />
        </SliderMask>

        <SliderText
          :slides="paddedSlides"
          :current-index="realIndex"
          :show-transitions="showTransitions"
        />
      </div>

      <button
        v-if="showNavElements"
        class="slider__button"
        :disabled="isTransitioning"
        data-track-text="appScreens.slider.previous"
        @click="onClick('next')"
      >
        <dotcom-icon
          name="chevron-right"
          art="regular"
          class="slider__caret-icon"
          :rotation="null"
          :color="
            isNextDisabled ? '--c-secondary-neutral-2' : '--c-primary-neutral-1'
          "
          hover-color="--c-primary-color-2"
        />
      </button>
    </div>

    <ul v-if="showNavElements" class="slider__dots">
      <li
        v-for="(slide, index) in slides"
        :key="index"
        :disabled="isTransitioning"
        data-track-text="appScreens.slider.dot"
        class="slider__dot"
        :class="{ 'slider__dot--active': index === targetIndex }"
        @click="onClick(index)"
      ></li>
    </ul>
  </div>
</template>

<script>
import { trackAction, tracker } from '@/helpers/tracker'

import SliderMask from './SliderMask'
import SliderMedia from './SliderMedia'
import SliderText from './SliderText'

const SWIPE_THRESHOLD = 80
const TRANSITION_DURATION = 800

export default {
  name: 'DotcomSlider',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    SliderMask,
    SliderMedia,
    SliderText,
  },
  props: {
    autoplay: {
      type: Boolean,
      default: true,
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    playBackRate: {
      type: String,
      default: '4000',
    },
    slides: {
      type: Array,
      default: () => [],
    },
    videoFallbackText: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isPreviousDisabled: false,
      isNextDisabled: false,
      slideIncrement: 1,
      showTransitions: true,
      isTransitioning: false,

      targetIndex: this.currentIndex,
      realIndex: this.currentIndex + 1,
    }
  },
  computed: {
    showNavElements() {
      return this.slides.length > 1
    },
    slidesCount() {
      return this.slides.length
    },
    paddedSlides() {
      return [
        this.slides[this.slides.length - 1],
        ...this.slides,
        this.slides[0],
      ]
    },
  },
  mounted() {
    if (this.autoplay) {
      this.loop()
    }
  },
  beforeUnmount() {
    this.stopLoop()
    clearTimeout(this.transitioningTimeout)
    clearTimeout(this.notransitionsTimeout)
  },
  methods: {
    onTransitionEnd() {
      this.isTransitioning = false

      if (
        this.realIndex !== 0 &&
        this.realIndex !== this.paddedSlides.length - 1
      ) {
        return
      }

      this.showTransitions = false

      this.$nextTick(() => {
        if (this.realIndex === 0) {
          this.realIndex = this.paddedSlides.length - 2
          this.targetIndex = this.slides.length - 1
        } else if (this.realIndex === this.paddedSlides.length - 1) {
          this.realIndex = 1
          this.targetIndex = 0
        }
        // nextTick doesn't work here. Even with two.
        this.transitioningTimeout = setTimeout(() => {
          this.showTransitions = true
        }, 100)
      })
    },

    goToPrevious() {
      this.isTransitioning = true
      this.notransitionsTimeout = setTimeout(
        this.onTransitionEnd.bind(this),
        TRANSITION_DURATION,
      )

      this.realIndex =
        this.realIndex - this.slideIncrement >= 0
          ? (this.realIndex -= this.slideIncrement)
          : this.slidesCount - this.slideIncrement
      this.targetIndex = Math.max(0, this.realIndex - 1)
    },
    goToNext() {
      this.isTransitioning = true
      this.notransitionsTimeout = setTimeout(
        this.onTransitionEnd.bind(this),
        TRANSITION_DURATION,
      )

      this.realIndex += this.slideIncrement
      this.targetIndex = this.realIndex - 1
    },
    goToIndex(slideIndex) {
      this.isTransitioning = true
      this.notransitionsTimeout = setTimeout(
        this.onTransitionEnd.bind(this),
        TRANSITION_DURATION,
      )

      this.realIndex = slideIndex + 1
      this.targetIndex = slideIndex
    },

    loop() {
      this.loopInterval = setInterval(
        this.goToNext,
        parseInt(this.playBackRate),
      )
    },
    stopLoop() {
      clearInterval(this.loopInterval)
    },

    onClick(target) {
      // Stop loop on user interaction. Even if nothing happens.
      this.stopLoop()

      // The loop is rather fragile. The transition must be completed before
      // triggering the next action or we might go out of bounds.
      if (this.isTransitioning) {
        return
      }

      if (target === 'previous') {
        this.goToPrevious()
      } else if (target === 'next') {
        this.goToNext()
      } else {
        this.goToIndex(target)
      }
    },

    onTouchStart(touchEvent) {
      if (this.isInvalidTouchEvent(touchEvent)) {
        return
      }
      const posXStart = touchEvent.changedTouches[0].clientX

      document.addEventListener(
        'touchend',
        touchEvent => this.onTouchEnd(touchEvent, posXStart),
        { once: true },
      )
    },
    onTouchEnd(touchEvent, posXStart) {
      if (this.isInvalidTouchEvent(touchEvent)) {
        return
      }
      const posXEnd = touchEvent.changedTouches[0].clientX
      const distanceX = Math.abs(posXEnd - posXStart)

      if (distanceX > SWIPE_THRESHOLD && posXStart < posXEnd) {
        this.onClick('previous')
        this.setTrackingData('swipeLeft')
      } else if (distanceX > SWIPE_THRESHOLD && posXStart > posXEnd) {
        this.onClick('next')
        this.setTrackingData('swipeRight')
      }

      document.removeEventListener('touchend', touchEvent =>
        this.onTouchEnd(touchEvent, touchEvent.changedTouches[0].clientX),
      )
    },
    isInvalidTouchEvent(touchEvent) {
      return touchEvent.changedTouches.length !== 1 || !this.showNavElements
    },

    onKeyDown(keyEvent) {
      switch (keyEvent.key) {
        case 'ArrowLeft':
          this.onClick('previous')
          break
        case 'ArrowRight':
          this.onClick('next')
          break
      }
    },

    setTrackingData(direction) {
      tracker.set({ data: { id: 'appScreens' } })
      trackAction(`appScreens.slider.${direction}`)
    },
  },
}
</script>

<style lang="postcss">
.slider {
  display: flex;
  flex-direction: column;
  outline: 0;

  &__content,
  &__stage {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__content {
    flex-direction: column;
    gap: 32px;
  }

  &__button {
    display: inline-block;
    outline: none;
    border: none;
    background-color: transparent;

    &:hover,
    &:focus {
      cursor: pointer;
    }
  }

  &__dots {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 76px;
    gap: 8px;

    li {
      list-style: none;
    }

    .slider__dot {
      padding: var(--spacing-2);
      display: block;
      width: var(--dotcom-app-screens-dot-dimensions);
      height: var(--dotcom-app-screens-dot-dimensions);
      padding: 0;
      background-color: #fff;
      list-style: none;
      border: 1px solid var(--c-secondary-neutral-2);
      border-radius: var(--dotcom-app-screens-dot-dimensions);
      overflow: hidden;
      transition: background-color 0.8s, border-color 0.8s;
      cursor: pointer;
    }
    .slider__dot--active {
      background-color: var(--c-primary-color-2);
      border-color: var(--c-primary-color-2);
    }
  }

  &__caret-icon {
    font-size: 25px;
  }

  .isDisabled {
    pointer-events: none;
    cursor: default;
  }
}

@media (--sm) {
  .slider {
    --dotcom-app-screens-dot-dimensions: 10px;

    .slider__content {
      padding: 0 var(--spacing-6);
    }
  }
}

@media (--md) {
  .slider {
    .slider__content {
      flex-direction: row;
    }
  }
}

@media (--lg) {
  .slider {
    .slider__content {
      padding: 0 var(--spacing-24);
    }
  }
}
</style>
